import { Carousel } from '@mantine/carousel'
import { Box, Card, Image, Skeleton, Stack, Text } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router.js'
import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import NextSVG from '../components/buttons/next_carousel_button.js'
import PreviousSVG from '../components/buttons/previous_carousel_button.js'
import TitleDivider from '../components/divider.js'
import ImageFallback from '../components/image_fallback.js'
import Layout from '../components/layout/index.js'
import { useAppContext } from '../context/app.js'
import styles from '../styles/Home.module.css'
import prerenderImportantLocales from '../utils/prerender_locales.js'

const limit = 100

const Search = () => {
  const { urlQuery } = useAppContext()

  const router = useRouter()
  const safeLocale = router.locale || 'en-US'
  const language = safeLocale.split('-')[0]
  const languageQuery = ['es', 'pt'].includes(language)
    ? `&language=${language}`
    : ''

  const [debounced] = useDebouncedValue(urlQuery, 400)
  const url = `/api/api_ext/content/multi_search?&type=all&sort=popularity&sort_order=desc${languageQuery}&q=`

  const fetcher = async (url) => {
    if (!debounced) return []
    const res = await fetch(url)
    const json = await res.json()
    return json
  }

  const { data, isLoading } = useSWR(`${url}${debounced}&limit=${limit}`, fetcher)

  const [isClient, setIsClient] = useState(false)
  const [subtitle, setSubtitle] = useState('Loading...')

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (debounced && data) {
      if (data.content?.length > 0 || data.people?.length > 0) {
        setSubtitle(`Found: “${debounced.toUpperCase()}”`)
      } else {
        setSubtitle(`Not found: “${debounced.toUpperCase()}”`)
      }
    }
  }, [debounced, data])

  const renderSkeleton = () => (
    <Box mt={24} sx={{ width: '80vw', maxWidth: '1366px', margin: '0 auto' }}>
      {Array(1)
        .fill(null)
        .map((_, index) => (
          <Skeleton
            height={30}
            width={100}
            radius="sm"
            animate
            mb={12}
            key={index}
          />
        ))}
      {Array(2)
        .fill(null)
        .map((_, index) => (
          <Carousel
            h={222}
            mt={12}
            mb={20}
            slideGap="xl"
            slideSize="12.5%"
            loop={false}
            align="start"
            slidesToScroll={1}
            nextControlIcon={null}
            previousControlIcon={null}
            styles={{ control: { display: 'none' } }}
            key={index}
          >
            {Array(8)
              .fill(null)
              .map((__, idx) => (
                <Carousel.Slide key={idx}>
                  <Skeleton height={230} width={150} radius="sm" animate />
                </Carousel.Slide>
              ))}
          </Carousel>
        ))}
    </Box>
  )

  const renderCarousel = (items, title, isPeople = false) => (
    <Stack spacing={0} mt={24}>
      <TitleDivider
        style={{
          zIndex: 1,
          textTransform: 'uppercase',
          margin: 0,
          padding: 0
        }}
        titleText={title}
      />
      <Carousel
        h={222}
        mt={12}
        mb={20}
        slideGap="xs"
        slideSize="10.5%"
        loop={false}
        align="start"
        nextControlIcon={<NextSVG />}
        previousControlIcon={<PreviousSVG />}
        slidesToScroll={2}
        styles={{
          control: {
            opacity: 10,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            border: 'transparent',
            transform: 'none',
            transition: 'none',
            '&[data-inactive]': {
              opacity: 0,
              cursor: 'default'
            }
          }
        }}
      >
        {items.map((item, index) => {
          const uniqueKey = isPeople
            ? `${item.name}-${index}`
            : `${item.id}-${index}`
          return isPeople ? (
            <Carousel.Slide key={uniqueKey}>
              {/* <Link href={`/people/${item.name}`} prefetch={false} passHref> */}
              <Card
                w={140}
                h={222}
                p={0}
                radius={4}
                sx={{
                  backgroundColor: '#0E1922',
                  borderBottom: '2px solid #7B39B6'
                }}
              >
                <Box sx={{ position: 'relative', width: '100%', height: '76%' }}>
                  {item.image ? (
                    <ImageFallback
                      src={item.image}
                      fallbackSrc="/userholder.png"
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      alt={`${item.name} - Person`}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0E1922',
                        color: '#fff'
                      }}
                    >
                      <Image
                        alt="person"
                        src="/userholder.png"
                        width={30}
                        height={30}
                      />
                    </Box>
                  )}
                </Box>
                <Stack p={12} spacing={0} align="center">
                  <Text size={12} tt="uppercase" color="white">
                    {item.name}
                  </Text>
                  {item.role && (
                    <Text size={10} tt="uppercase" fw="bolder" color="gray">
                      {item.role}
                    </Text>
                  )}
                </Stack>
              </Card>
              {/* </Link> */}
            </Carousel.Slide>
          ) : (
            <Carousel.Slide key={uniqueKey}>
              <Card
                w={140}
                h={222}
                p={0}
                m={0}
                style={{ width: '140px', height: '222px' }}
                radius={4}
                sx={{
                  backgroundColor: '#0E1922',
                  borderBottom: '2px solid #7B39B6'
                }}
              >
                <Link
                  href={`/${item.type.toLowerCase()}/${
                    item.slug ? item.slug : item.uid
                  }`}
                  prefetch={false}
                  passHref
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative'
                    }}
                  >
                    <ImageFallback
                      src={item.poster}
                      fallbackSrc="/noimage.png"
                      alt={item.title}
                      width={140}
                      height={222}
                    />
                  </div>
                </Link>
              </Card>
            </Carousel.Slide>
          )
        })}
      </Carousel>
    </Stack>
  )

  const renderContent = () => {
    if (isLoading || !isClient) {
      return renderSkeleton()
    }

    if (data) {
      const movies = data.content?.filter(
        (m) => m.type === 'movie' || m.type === 'Movie'
      )
      const series = data.content?.filter(
        (m) => m.type === 'series' || m.type === 'Series'
      )
      const people = data.people || []

      return (
        <>
          {movies?.length > 0 && renderCarousel(movies, 'MOVIES')}
          {series?.length > 0 && renderCarousel(series, 'SERIES')}
          {people?.length > 0 && renderCarousel(people, 'PEOPLE', true)}
        </>
      )
    }

    return null
  }

  return (
    <Layout
      showSearchBar
      withAutocomplete
      showHero={true}
      heroSubtitle={subtitle}
      pageName="Search"
    >
      <Head>
        <title>Search Results - Stream Discover</title>
        <meta name="description" content="Search results for various contents" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <main className={styles.main}>
        <Stack
          style={{
            width: '80vw',
            maxWidth: '1366px',
            margin: '0 auto',
            gap: 0
          }}
          spacing={0}
        >
          {renderContent()}
        </Stack>
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }) {
  prerenderImportantLocales(locale)

  return {
    props: {}
  }
}

export default Search
